import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { fetchMatches } from '../api/matches';
import { useLivescoresOptions } from '../store';
import type { LivescoreTab } from '../store/types';

import { useFilters } from './filters';

import type { Match } from '@shared-types/match.types';

export const useLivescoresMatches = (tab: LivescoreTab) => {
  const { options } = useLivescoresOptions();
  const { filters } = useFilters();

  const [matches, setMatches] = useState<Match[]>([]);
  const updateMatch = (payload: Match) => {
    setMatches((state) => {
      const index = state.findIndex((match) => match.id === payload.id);
      if (index === -1) return state;
      state[index] = payload;
      return [...state];
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['livescore-matches', { ...options, ...filters }, tab],
    queryFn: ({ signal }) => {
      if (!tab) return;

      const date = dayjs(tab.date);
      if (tab.draw && filters.competition) {
        return fetchMatches(
          {
            competitionIds: [filters.competition.id],
            roundId: tab.draw.rounds?.map((md) => md.roundId),
            fromDate: date,
            toDate: date,
          },
          signal,
        );
      }

      return fetchMatches(
        {
          competitionIds: filters.competition ? [filters.competition.id] : options.competitionIds,
          fromDate: date,
          toDate: date,
        },
        signal,
      );
    },
    enabled: !!tab,
    placeholderData: [],
  });

  useEffect(() => {
    if (!data) return;

    setMatches(data);
  }, [data, setMatches]);

  const result = useMemo(() => {
    if (!tab) return [];

    return matches.filter((m) => dayjs(m.kickOffTime.dateTime ?? m.kickOffTime.date).isSame(tab.date, 'day'));
  }, [matches, tab]);

  return { matches: result, isLoading, updateMatch };
};
